import apiService from "src/services/ApiCallService";
import { useEffect, useState } from "react";
import ErrorUtils from "src/utils/ErrorUtils";
import {
    AddSegmentColumnsRequest,
    AddSegmentColumnsResponse,
    ColumnDef,
    CreateSegmentDataTableRequest,
    CreateSegmentDataTableResponse,
    DownloadSegmentDataTableResponse,
    DownloadSegmentUploadTemplateResponse,
    GetCDTGLPullStatusResponse,
    GetSegmentAggregatesResponse,
    GetSegmentDataTableResponse,
    GetSegmentPeriodsResponse,
    GetSegmentValidationsResponse,
    ListSegmentDataTablesRequest,
    ListSegmentDataTablesResponse,
    PullStatusResponse,
    SearchSegmentDataTableRowsRequest,
    SearchSegmentDataTableRowsResponse,
    TriggerCDTGLPullResponse,
    UploadSegmentDataTableResponse,
} from "src/models/segment-cdt/SegmentCdtTypes";
import { SEGMENT_DETAIL_PAGE_SIZE, SegmentRowsPage } from "src/services/segment-cdt/SegmentTablesState";
import CONSTANTS from "src/utils/constants";

/** A period-scoped read request shared by the validate + download hooks. */
export interface SegmentPeriodRequest {
    tableId: string;
    period: string;
}


/**
 * Service for Segment-Level Custom Data Tables. One place for every segment-CDT operation.
 * Hooks follow the house `[result, loading, error]` shape used across ATP services.
 */
export default class SegmentDataTablesService {

    /**
     * Lists segment data tables. Backed by `ListSegmentDataTables` (POST /list/segment-data-tables).
     */
    listSegmentDataTables(request?: ListSegmentDataTablesRequest): [ListSegmentDataTablesResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as ListSegmentDataTablesResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.listSegmentDataTables(request);
                    const json = await response.json() as ListSegmentDataTablesResponse;
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (request != null) {
                fetchData();
            }
        }, [request]);

        return [result, loading, error];
    }

    /**
     * Creates a segment data table. Backed by `CreateSegmentDataTable` (POST /create/segment-data-table),
     * which writes the METADATA + LEGEND rows in one transaction (no rows and no GL pull happen at
     * create). Imperative (not a render hook); the caller owns loading / error / double-submit.
     */
    async createSegmentDataTable(request: CreateSegmentDataTableRequest): Promise<CreateSegmentDataTableResponse> {
        const response = await apiService.createSegmentDataTable(request);
        return await response.json() as CreateSegmentDataTableResponse;
    }

    /**
     * Adds one or more columns to an existing segment data table. Backed by `AddSegmentColumns`
     * (POST /segment-data-table/add-columns). The response carries the full effective column set, so
     * the caller can re-render the grid without a follow-up fetch. Imperative; the caller owns
     * loading / error / double-submit.
     */
    async addSegmentColumns(tableId: string, columns: ColumnDef[]): Promise<AddSegmentColumnsResponse> {
        const payload: AddSegmentColumnsRequest = { tableId, columns };
        const response = await apiService.addSegmentColumns(payload);
        return await response.json() as AddSegmentColumnsResponse;
    }

    /**
     * Fetches one segment table's definition (metadata + column defs). Backed by `GetSegmentDataTable`
     * (POST /get/segment-data-table). Fetches only when `tableId` is set.
     */
    getSegmentDataTable(tableId?: string): [GetSegmentDataTableResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as GetSegmentDataTableResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let ignore = false; // drop a stale response if tableId changes before this resolves
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.getSegmentDataTable(tableId as string);
                    const json = await response.json() as GetSegmentDataTableResponse;
                    if (!ignore) setResult(json);
                } catch (ex) {
                    if (!ignore) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!ignore) setLoading(false);
                }
            }
            if (tableId) {
                fetchData();
            }
            return () => { ignore = true; };
        }, [tableId]);

        return [result, loading, error];
    }

    /**
     * Lists the periods that hold rows for a table. Backed by `GetSegmentPeriods`
     * (POST /segment-data-table/periods). Fetches only when `tableId` is set. Returns a 404 until the
     * serving activity ships, which surfaces as the error slot + an empty period list. `refreshNonce`
     * re-fetches the list after a write (a GL pull / upload can materialize a new period), so the period
     * selector doesn't go stale.
     */
    getSegmentPeriods(tableId?: string, refreshNonce?: number): [GetSegmentPeriodsResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as GetSegmentPeriodsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let ignore = false; // drop a stale response if tableId changes before this resolves
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.getSegmentPeriods(tableId as string);
                    const json = await response.json() as GetSegmentPeriodsResponse;
                    if (!ignore) setResult(json);
                } catch (ex) {
                    if (!ignore) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!ignore) setLoading(false);
                }
            }
            if (tableId) {
                fetchData();
            }
            return () => { ignore = true; };
        }, [tableId, refreshNonce]);

        return [result, loading, error];
    }

    /**
     * Searches one period's rows with numbered paging. Backed by `SearchSegmentDataTableRows`
     * (POST /search/segment-data-table-rows). Fetches only when `request` is set. Derives `pagesCount`
     * from the server's exact `totalSize`, the same `ceil(totalSize / pageSize)` the CLI records grid uses.
     */
    searchSegmentDataTableRows(request?: SearchSegmentDataTableRowsRequest): [SegmentRowsPage, boolean, string] {
        const [result, setResult] = useState(null as unknown as SegmentRowsPage);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            // Drop a stale response: rapid page/sort/filter changes start overlapping fetches, and
            // without this an earlier one could resolve last and overwrite the newer page.
            let ignore = false;
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.searchSegmentDataTableRows(request as SearchSegmentDataTableRowsRequest);
                    const json = await response.json() as SearchSegmentDataTableRowsResponse;
                    const pageSize = json.pageSize || request?.pageSize || SEGMENT_DETAIL_PAGE_SIZE;
                    // Prefer the exact totalSize; if the server omits it, degrade gracefully off hasMore
                    // rather than collapsing a multi-page result to a single page.
                    const page = json.page || 1;
                    const pagesCount = json.totalSize != null
                        ? Math.max(1, Math.ceil(json.totalSize / pageSize))
                        : (json.hasMore ? page + 1 : page);
                    if (!ignore) setResult({ ...json, pagesCount });
                } catch (ex) {
                    if (!ignore) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!ignore) setLoading(false);
                }
            }
            if (request != null) {
                fetchData();
            }
            return () => { ignore = true; };
        }, [request]);

        return [result, loading, error];
    }

    /**
     * Validates one period on demand. Backed by `GetSegmentValidations` (POST /segment-data-table/validations).
     * Fetches only when `request` is set; returns a fresh report whenever `request` changes.
     */
    getSegmentValidations(request?: SegmentPeriodRequest): [GetSegmentValidationsResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as GetSegmentValidationsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let ignore = false;
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.getSegmentValidations(request!.tableId, request!.period);
                    const json = await response.json() as GetSegmentValidationsResponse;
                    if (!ignore) setResult(json);
                } catch (ex) {
                    if (!ignore) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!ignore) setLoading(false);
                }
            }
            if (request != null) {
                fetchData();
            }
            return () => { ignore = true; };
        }, [request]);

        return [result, loading, error];
    }

    /**
     * The selected period's server-computed per-column totals. Backed by `GetSegmentAggregates`
     * (POST /segment-data-table/aggregates), whose `tableId` and `period` are both required — so this
     * fetches only once a period is selected, and returns nothing before then.
     *
     * Keyed on `refreshNonce` as well as the period so a write re-reads the totals: a GL pull or an upload
     * changes them, and they are computed on read rather than stored, so re-calling is the only way to see
     * the new figures. Callers pass `state.detail.refreshToken`, the same token that re-runs the row search,
     * which keeps rows and totals from disagreeing.
     */
    getSegmentAggregates(tableId?: string, period?: string, refreshNonce?: number):
        [GetSegmentAggregatesResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as GetSegmentAggregatesResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let ignore = false; // drop a stale response if the period changes before this resolves
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.getSegmentAggregates(tableId as string, period as string);
                    const json = await response.json() as GetSegmentAggregatesResponse;
                    if (!ignore) setResult(json);
                } catch (ex) {
                    if (!ignore) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!ignore) setLoading(false);
                }
            }
            if (tableId && period) {
                fetchData();
            }
            return () => { ignore = true; };
        }, [tableId, period, refreshNonce]);

        return [result, loading, error];
    }

    /**
     * Fetches a presigned download URL for a period's data. Backed by `DownloadSegmentDataTable`
     * (POST /segment-data-table/download). Fetches only when `request` is set; the caller opens the URL.
     */
    downloadSegmentDataTable(request?: SegmentPeriodRequest): [DownloadSegmentDataTableResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as DownloadSegmentDataTableResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let ignore = false;
            async function fetchData() {
                setLoading(true);
                setError('');
                try {
                    const response = await apiService.downloadSegmentDataTable(request!.tableId, request!.period);
                    const json = await response.json() as DownloadSegmentDataTableResponse;
                    if (!ignore) setResult(json);
                } catch (ex) {
                    if (!ignore) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!ignore) setLoading(false);
                }
            }
            if (request != null) {
                fetchData();
            }
            return () => { ignore = true; };
        }, [request]);

        return [result, loading, error];
    }

    /**
     * Fetches a presigned URL for a period's prefilled upload template. Backed by
     * `DownloadSegmentUploadTemplate` (POST /segment-data-table/upload-template). Imperative (a button
     * click), returning the URL so the caller opens it; the template is the input to
     * `uploadSegmentDataTable`. Distinct from `downloadSegmentDataTable`, which returns the review workbook.
     */
    async downloadSegmentUploadTemplate(tableId: string, period: string): Promise<string> {
        const response = await apiService.downloadSegmentUploadTemplate(tableId, period);
        const json = await response.json() as DownloadSegmentUploadTemplateResponse;
        return json.downloadUrl;
    }

    /**
     * Triggers a full-table GL pull for a period. Backed by `TriggerCDTGLPull`
     * (POST /segment-data-table/trigger-gl-pull → 202). Imperative; returns the pullRequestId to poll.
     */
    async triggerCDTGLPull(tableId: string, period: string): Promise<string> {
        const response = await apiService.triggerCDTGLPull(tableId, period);
        const json = await response.json() as TriggerCDTGLPullResponse;
        return json.pullRequestId;
    }

    /**
     * Reads a pull's status. Backed by `GetCDTGLPullStatus` (POST /segment-data-table/gl-pull-status).
     * Imperative so the caller can poll on its own cadence until a terminal state (COMPLETED / FAILED).
     */
    async getCDTGLPullStatus(pullRequestId: string): Promise<PullStatusResponse | undefined> {
        const response = await apiService.getCDTGLPullStatus(pullRequestId);
        const json = await response.json() as GetCDTGLPullStatusResponse;
        return json.pullStatus;
    }

    /**
     * Reads whichever GL pull is currently in flight for a table + period. Backed by the same
     * `GetCDTGLPullStatus` op in its tableId/period form — the answer a page uses on load (or on a
     * period switch) to discover an in-progress pull it didn't start. Resolves `undefined` when the
     * table is idle (the backend returns an absent `pullStatus`, not an error).
     */
    async getCDTGLPullStatusByTable(tableId: string, period: string): Promise<PullStatusResponse | undefined> {
        const response = await apiService.getCDTGLPullStatus(undefined, tableId, period);
        const json = await response.json() as GetCDTGLPullStatusResponse;
        return json.pullStatus;
    }

    /**
     * Uploads an .xlsx of manual values for a period: puts the file in S3 (AWS SDK) then commits it via
     * `UploadSegmentDataTable` (POST /segment-data-table/upload). The backend resolves the period itself.
     * Imperative; the caller owns the modal + loading. Even a refused upload returns 200 with counters.
     */
    async uploadSegmentDataTable(tableId: string, file: File): Promise<UploadSegmentDataTableResponse> {
        // Sanitize the interpolated parts: tableId is base64-decoded (can hold '/', '+', '=') and the
        // file name is arbitrary, either of which would otherwise create unintended S3 prefixes or a key
        // the commit endpoint parses differently. Keep only key-safe characters.
        const safe = (s: string) => s.replace(/[^a-zA-Z0-9._-]/g, '_');
        const key = `${new Date().getTime()}_segment_${safe(tableId)}_${safe(file.name)}`;
        await apiService.uploadToS3(CONSTANTS.ENVIRONMENT_VARIABLES.CUSTOM_TABLE_UPLOAD_BUCKET, key, file);
        // The upload op takes only { tableId, s3Key }; the backend resolves the period and echoes it back.
        const response = await apiService.uploadSegmentDataTable(tableId, key);
        return await response.json() as UploadSegmentDataTableResponse;
    }
}
